import React, { useContext, useEffect, useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip, Popover, Badge } from "@material-ui/core";
import {
  Bot,
  KanbanSquare,
  LayoutDashboard,
  MessagesSquare,
  Tag,
  Zap,
} from "lucide-react";
import { AuthContext } from "../context/Auth/AuthContext";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import NotificationsPopOver from "../components/NotificationsPopOver";
import ChatPopover from "../pages/Chat/ChatPopover";
import TasksPopover from "../components/TasksPopover";
import useTaskNotifications from "../hooks/useTaskNotifications";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import { Schedule, Description, GridOn } from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { Can } from "../components/Can";
import NotificationKanban from "../components/NotificationKanban";

// ErrorBoundary movido para fora do componente principal
class MyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.log("Error boundary catch:", error, errorInfo);
    // Aqui você pode reportar erros a um serviço externo
  }
  render() {
    if (this.state.hasError) {
      return <h1>Ops! Algo deu errado.</h1>;
    }
    return this.props.children;
  }
}

export default function AppListItems({ volume, setVolume }) {
  // Constantes para índices de botões
  const GERENCIA_BUTTON_INDEX = 2;

  const [anchorEl, setAnchorEl] = useState(null);
  // menuOpen removido (não utilizado)
  // selectedSubMenu agora é objeto
  const [selectedSubMenu, setSelectedSubMenu] = useState({});
  const [activeButton, setActiveButton] = useState(null);

  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { notificationCount } = useTaskNotifications();

  const [isMobile, setIsMobile] = useState(false);
  
  // Estados para o popup de tarefas
  const [tasksPopoverAnchor, setTasksPopoverAnchor] = useState(null);
  const [showTasksPopover, setShowTasksPopover] = useState(false);
  const [showTasksInfoPopover, setShowTasksInfoPopover] = useState(false);
  // SLA Popover controlado no ícone Kanban
  const [kanbanAnchorEl, setKanbanAnchorEl] = useState(null);
  const [kanbanPopoverOpen, setKanbanPopoverOpen] = useState(false);
  const [slaCount, setSlaCount] = useState(0);
  const [unlockKanbanAudio, setUnlockKanbanAudio] = useState(null);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1300);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Exibe o balão "Conheça nosso novo Sistema de Tarefas" ao acessar/recarregar
  useEffect(() => {
    // Agora mostra apenas se estiver na página de Atendimentos (/tickets)
    if (window.location.pathname === '/tickets') {
      setShowTasksInfoPopover(true);
      const timeout = setTimeout(() => setShowTasksInfoPopover(false), 30000); // 30 segundos
      return () => clearTimeout(timeout);
    }
  }, []);

  // Função utilitária para role
  function getUserRole(user) {
    if (!user) return null;
    if (
      (user.profile === "user" && user.showDashboard === "enabled") ||
      user.allowRealTime === "enabled"
    ) {
      return "admin";
    }
    return user.profile;
  }

  const handleMenuClick = (event, buttonIndex) => {
    setAnchorEl(event.currentTarget);
    setActiveButton(buttonIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveButton(null);
  };

  const handleSubMenuClick = (submenu, buttonIndex) => {
    setSelectedSubMenu((prev) => ({ ...prev, [buttonIndex]: submenu }));
    handleMenuClose();
  };

  const handleTasksButtonRef = (node) => {
    if (node) setTasksPopoverAnchor(node);
  };

  const handleTasksClick = (event) => {
    event.preventDefault();
    setShowTasksPopover(true);
  };

  const handleTasksPopoverClose = () => {
    setShowTasksPopover(false);
  };

  // Clique no badge de SLA do Kanban abre o popover; clique no botão continua navegando
  const handleKanbanIconRef = (node) => {
    if (node) setKanbanAnchorEl(node);
  };

  const handleOpenKanbanSla = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (typeof unlockKanbanAudio === 'function') {
        unlockKanbanAudio();
      } else {
        // Tentativa genérica de desbloqueio: reutiliza contexto único se disponível
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          if (!window.__globalAudioCtx) {
            try { window.__globalAudioCtx = new Ctx(); } catch(_) {}
          }
          const ctx = window.__globalAudioCtx;
          if (ctx && ctx.state === 'suspended') {
            try { ctx.resume(); } catch(_) {}
          }
        }
      }
    } catch (_) {}
    setKanbanPopoverOpen(true);
  };

  const handleCloseKanbanSla = () => setKanbanPopoverOpen(false);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 5,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <MyErrorBoundary>
          <NotificationsPopOver
            volume={volume}
            style={{ width: "30px", height: "25px", marginRight: "5px" }}
          />
        </MyErrorBoundary>
        <Tooltip title={i18n.t("appBarListem.ticket")}>
          <IconButton
            component={RouterLink}
            to="/tickets"
            size="small"
            aria-label="Atendimentos"
            style={{
              borderRadius: selectedSubMenu[GERENCIA_BUTTON_INDEX] ? 5 : 80,
              paddingInline: selectedSubMenu[GERENCIA_BUTTON_INDEX] ? "5px" : 2,
              fontSize: "14px",
              color: "#FFF",
              fontFamily: "Poppins",
              display: "flex",
              alignItems: "center",
              marginLeft: "-3px",
            }}
          >
            {"Atendimentos"}
          </IconButton>
        </Tooltip>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={i18n.t("appBarListem.kanban")}> 
          <Badge
            color="secondary"
            overlap="circular"
            badgeContent={slaCount > 9 ? '9+' : slaCount}
            invisible={slaCount === 0}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            style={{ marginRight: 1, cursor: 'pointer' }}
          >
            <IconButton
              size="small"
              aria-label="Abrir alertas do Kanban"
              style={{
                borderRadius: 80,
                paddingInline: 1,
                fontSize: "14px",
                color: "#FFF",
                fontFamily: "Poppins",
              }}
              onClick={handleOpenKanbanSla}
              ref={handleKanbanIconRef}
            >
              <KanbanSquare style={{ width: "30px", height: "25px" }} />
            </IconButton>
          </Badge>
        </Tooltip>
        <RouterLink 
          to="/kanban"
          style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginLeft: 2,
            cursor: 'pointer',
            color: '#FFF',
            fontSize: '14px',
            fontFamily: 'Poppins',
            textDecoration: 'none'
          }}
          aria-label="Ir para Kanban"
        >
          Kanban
        </RouterLink>
        {/* Popover ancorado ao ícone Kanban (SLA apenas) */}
        <NotificationKanban
          anchorEl={kanbanAnchorEl}
          open={kanbanPopoverOpen}
          onClose={handleCloseKanbanSla}
          onCountChange={setSlaCount}
          volume={volume}
          registerUnlock={setUnlockKanbanAudio}
        />
      </div>
      <Tooltip title={i18n.t("appBarListem.contacts")}>
        <IconButton
          component={RouterLink}
          to="/contacts"
          size="small"
          aria-label="Contatos"
          style={{
            borderRadius: 80,
            paddingInline: 2,
            fontSize: "14px",
            color: "#FFF",
            fontFamily: "Poppins",
          }}
        >
          <ContactPhoneOutlinedIcon style={{ width: "30px", height: "25px" }} />
          {"Contatos"}
        </IconButton>
      </Tooltip>
      <Tooltip title={i18n.t("appBarListem.schedule")}>
        <IconButton
          component={RouterLink}
          to="/schedules"
          size="small"
          aria-label="Agendamentos"
          style={{
            borderRadius: 80,
            paddingInline: 2,
            fontSize: "14px",
            color: "#FFF",
            fontFamily: "Poppins",
          }}
        >
          <Schedule style={{ width: "30px", height: "25px" }} />
          {"Agendamentos"}
        </IconButton>
      </Tooltip>
      <div style={{ display: "flex", alignItems: "center" }}>
        <ChatPopover style={{ width: "30px", height: "25px", marginRight: "5px" }} />
        {/* Tooltip deve envolver apenas o IconButton, Popover fica fora */}
        <Tooltip title={i18n.t("appBarListem.chats")}> 
          <span>
            <IconButton
              component={RouterLink}
              to="/chats"
              size="small"
              aria-label="Chat Interno"
              style={{
                borderRadius: selectedSubMenu[GERENCIA_BUTTON_INDEX] ? 5 : 80,
                paddingInline: selectedSubMenu[GERENCIA_BUTTON_INDEX] ? "5px" : 2,
                fontSize: "14px",
                color: "#FFF",
                fontFamily: "Poppins",
                display: "flex",
                alignItems: "center",
                marginLeft: "-3px",
                position: 'relative',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Chat
                <sup style={{
                  fontSize: '8px', // um pouco menor
                  color: '#FFD600',
                  marginLeft: 1, // mais próximo do texto
                  fontWeight: 600,
                  verticalAlign: 'text-top', // mais acima
                  letterSpacing: 0.2,
                  position: 'relative',
                  top: '-2px', // eleva mais
                }}>beta</sup>
              </span>
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <Tooltip title="Tarefas">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Badge 
            badgeContent={notificationCount} 
            color="error"
            invisible={notificationCount === 0}
            style={{ cursor: 'pointer', marginRight: 1 }}
          >
            <IconButton
              size="small"
              aria-label="Ver resumo de tarefas"
              style={{
                borderRadius: 80,
                paddingInline: 1,
                fontSize: "14px",
                color: "#FFF",
                fontFamily: "Poppins",
                display: "flex",
                alignItems: "center",
              }}
              onClick={handleTasksClick}
              ref={handleTasksButtonRef}
            >
              <EventAvailableIcon style={{ width: "30px", height: "25px" }} />
            </IconButton>
          </Badge>
          {!isMobile && (
            <RouterLink 
              to="/tasks"
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                marginLeft: 2,
                cursor: 'pointer',
                color: '#FFF',
                fontSize: '14px',
                fontFamily: 'Poppins',
                textDecoration: 'none'
              }}
            >
              Tarefas
              <sup style={{
                fontSize: '8px',
                color: '#FFD600',
                marginLeft: 1,
                fontWeight: 600,
                verticalAlign: 'text-top',
                letterSpacing: 0.2,
                position: 'relative',
                top: '-2px',
              }}>beta</sup>
            </RouterLink>
          )}
        </div>
      </Tooltip>
      
      {/* Popup de Tarefas */}
      <TasksPopover
        anchorEl={tasksPopoverAnchor}
        open={showTasksPopover}
        onClose={handleTasksPopoverClose}
      />
      
      {user && (
        <Can
          role={getUserRole(user)}
          perform={"drawer-admin-items:view"}
          yes={() => (
            <Tooltip title={i18n.t("appBarListem.reports")}>
              <IconButton
                size="small"
                aria-label="Gerência"
                style={{
                  borderRadius: 80,
                  paddingInline: 2,
                  fontSize: "14px",
                  color: "#FFF",
                  fontFamily: "Poppins",
                }}
                onClick={(event) => handleMenuClick(event, GERENCIA_BUTTON_INDEX)}
              >
                <LayoutDashboard style={{ width: "30px", height: "25px" }} />
                {!isMobile && <span>Gerência</span>}
              </IconButton>
            </Tooltip>
          )}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        getContentAnchorEl={null}
        open={activeButton === GERENCIA_BUTTON_INDEX}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div
          style={{
            margin: "-5px 5px",
            display: "flex",
            backgroundColor: "transparent",
            justifyContent: "center",
          }}
        >
          <Can
            role={user && user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile}
            perform={"drawer-admin-items:view"}
            yes={() => ([
                <MenuItem
                  key="menu-dashboard"
                  component={RouterLink}
                  to="/dashboard"
                  onClick={handleMenuClose}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100px",
                    textAlign: "center",
                    margin: "0 auto",
                  }}
                >
                  <DashboardOutlinedIcon style={{ fontSize: "25px", marginBottom: "4px" }} />
                  <span style={{ fontSize: "12px" }}>Dashboard</span>
                </MenuItem>,
                <MenuItem
                  key="menu-reports"
                  component={RouterLink}
                  to="/reports"
                  onClick={handleMenuClose}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100px",
                    textAlign: "center",
                    margin: "0 auto",
                  }}
                >
                  <Description style={{ fontSize: "25px", marginBottom: "4px" }} />
                  <span style={{ fontSize: "12px" }}>Relatórios</span>
                </MenuItem>
              ])}
          />
          <Can
            role={user && user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile}
            perform={"drawer-admin-items:view"}
            yes={() => (
              <MenuItem
                component={RouterLink}
                to="/moments"
                onClick={handleMenuClose}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100px",
                  textAlign: "center",
                  margin: "0 auto",
                }}
              >
                <GridOn style={{ fontSize: "25px", marginBottom: "4px" }} />
                <span style={{ fontSize: "12px", wordWrap: "break-word", whiteSpace: "normal" }}>
                  Painel de Atendimentos
                </span>
              </MenuItem>
            )}
          />
        </div>
      </Menu>
    </div>
  );
}
